import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Page } from '../theme/elements/Page';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getUserDetails } from 'state/reducers/user.reducers';
import { getTimeOfDay, greetingEnum } from 'constants/global.constants';
import { getBoSignerAllBalanceRequests, getBoSignerBalanceRequestsByStatus } from 'services/pending-requests.service';
import { permissionEnums } from 'constants/permission.constants';
import { hasPermission } from 'components/AuthorizationComponent';
import { SearchComponent } from 'components/SearchComponent/SearchComponent';
import { BONewRequests } from 'sections/Dashboard/BoSigner/NewRequests';
import {
	getBoSignerPendingRequests,
	getBoSignerBalanceRequestsByStatusData,
} from 'state/reducers/pending-requests.reducer';
import { BORequests } from 'sections/Dashboard/BoSigner/OtherRequests';
import { SignerDashboardTableType } from 'constants/requests.constants';
import { BalanceStatus } from 'constants/balance.constants';
import _ from 'lodash';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';
import { useServerPagination } from 'hooks/pagination/useServerPagination';

const COMPLETED_INITIAL_SORT_BY = 'createdAt';
const COMPLETED_INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;
const sortFieldMap: Record<string, string> = {
	boApprovalDate: 'boApprovalDate',
	boSecondarySignerApprovalDate: 'boSecondarySignerApprovalDate',
	createdAt: 'created_at',
	status: 'status',
};
const mapSortBy = (sortBy: string) => sortFieldMap[sortBy] || 'created_at';

export const BoSignerDashboard = () => {
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const [pageGreeting, setPageGreeting] = useState('');
	const { firstName } = useAppSelector(getUserDetails);
	const pagePermission = hasPermission([permissionEnums.CLIENTS_ACTIVITY_DASHBOARD]);
	const boSignerRequestsPermission = hasPermission([permissionEnums.BO_SIGNER_DASHBOARD_REQUESTS]);
	const searchPermission = hasPermission([permissionEnums.FREE_TEXT_SEARCH_DATABASE]);
	const pagePrefix = 'page.dashboard.search.';
	const boSignerPendingRequests = useAppSelector(getBoSignerPendingRequests);
	const boSignerCompletedRequests = useAppSelector((state) =>
		getBoSignerBalanceRequestsByStatusData(state, BalanceStatus.Completed)
	);
	const isRequestsAccessible = useMemo(() => !!boSignerPendingRequests, [boSignerPendingRequests]);

	const fetchCompletedRequests = useCallback(
		async (page: number, sortOverride: { sortBy: string; sortOrder: Order }) => {
			await dispatch(
				getBoSignerBalanceRequestsByStatus({
					status: BalanceStatus.Completed,
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
					sortBy: mapSortBy(sortOverride.sortBy),
					sortOrder: sortOverride.sortOrder,
				})
			);
		},
		[dispatch]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination({
		initialSortBy: COMPLETED_INITIAL_SORT_BY,
		initialSortOrder: COMPLETED_INITIAL_SORT_ORDER,
		fetchPage: fetchCompletedRequests,
	});

	// Initial data fetch - run both in parallel for better performance
	useEffect(() => {
		if (!pagePermission || !boSignerRequestsPermission) return;

		setTimeOfDay();

		Promise.all([
			dispatch(getBoSignerAllBalanceRequests()),
			runFetch(0, { sortBy: COMPLETED_INITIAL_SORT_BY, sortOrder: COMPLETED_INITIAL_SORT_ORDER }),
		]);
	}, [pagePermission, boSignerRequestsPermission, dispatch, runFetch]);

	const setTimeOfDay = () => {
		switch (getTimeOfDay()) {
			case greetingEnum.MORNING:
				setPageGreeting(`${t('greeting.morning')}`);
				break;
			case greetingEnum.NOON:
				setPageGreeting(`${t('greeting.noon')}`);
				break;
			case greetingEnum.AFTERNOON:
				setPageGreeting(`${t('greeting.afternoon')}`);
				break;
			case greetingEnum.EVENING:
				setPageGreeting(`${t('greeting.evening')}`);
				break;
			default:
				setPageGreeting(`${t('greeting.hello')}`);
				break;
		}
	};
	return (
		<Fragment>
			<Page title="">
				<Stack spacing={7}>
					<Stack spacing={4}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
							<Typography variant="pageTitle">
								{pageGreeting}, {firstName}
							</Typography>
						</Box>
						{searchPermission && (
							<Stack spacing={3}>
								<Box>
									<Typography
										variant="h4"
										sx={{ fontWeight: 700, color: theme.palette.common.black }}
									>
										{t(`${pagePrefix}title`)}
									</Typography>
									<Typography variant="subtitle" sx={{ color: theme.palette.root.grey }}>
										{t(`${pagePrefix}subtitle`)}
									</Typography>
								</Box>
								<Stack spacing={4}>
									<SearchComponent />
									<Divider
										orientation="horizontal"
										flexItem
										sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
									/>
								</Stack>
							</Stack>
						)}
					</Stack>
					{boSignerRequestsPermission && isRequestsAccessible && (
						<>
							<Stack spacing={4}>
								<BONewRequests
									requests={_.reverse(_.sortBy(boSignerPendingRequests?.Pending, 'createdAt'))}
								/>
								<Divider
									orientation="horizontal"
									flexItem
									sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
								/>
							</Stack>
							{/*
							<Stack spacing={4}>
								<BORequests
									title={t('page.dashboard.boSigner.inProgressRequests.title')}
									subtitle={`${boSignerPendingRequests?.In_Progress?.length} ${t(
										'page.dashboard.boSigner.inProgressRequests.subtitle'
									)}`}
									requests={_.reverse(_.sortBy(boSignerPendingRequests?.In_Progress, 'createdAt'))}
									type={SignerDashboardTableType.IN_PROGRESS_REQUESTS}
								/>
								<Divider
									orientation="horizontal"
									flexItem
									sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
								/>
							</Stack> */}

							<Stack spacing={4}>
								<BORequests
									title={t('page.dashboard.boSigner.completedRequests.title')}
									subtitle={`${boSignerCompletedRequests.pagination.total} ${t(
										'page.dashboard.boSigner.completedRequests.subtitle'
									)}`}
									requests={boSignerCompletedRequests.data}
									type={SignerDashboardTableType.COMPLETED_REQUESTS}
									pagination={{
										total: boSignerCompletedRequests.pagination.total,
										hasMore: boSignerCompletedRequests.pagination.hasMore,
										currentPage,
										onPageChange: handlePageChange,
										onSortChange: handleSortChange,
										isLoading,
									}}
								/>
								<Divider
									orientation="horizontal"
									flexItem
									sx={{ borderBottom: 1, borderColor: theme.palette.root.lightGreen }}
								/>
							</Stack>
						</>
					)}
				</Stack>
			</Page>
		</Fragment>
	);
};
